import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatCurrency, formatDateTime, formatRelativeDate } from '@/shared/lib/format';
import { DEFAULT_TIME_ZONE } from '@/shared/lib/time-zone';

import { useRefreshProviderBalance } from '../api/providers.queries';
import type { Provider } from '../types/provider.types';

/**
 * The provider's balance. For a provider with a balance API it is what the
 * provider last reported (the worker reads it every two hours); opening the
 * page never calls the provider, and "read now" asks it once. A balance that is
 * not current is shown as such, never as zero or low.
 */
export function ProviderBalance({ provider }: { provider: Provider }) {
  const refresh = useRefreshProviderBalance();
  const currency = provider.balanceCurrency ?? provider.currencyCode ?? 'USD';
  const neverRead = provider.hasBalanceApi && provider.balanceCheckedAtUtc === null;

  return (
    <div>
      <dt className="text-muted-foreground flex items-center gap-1 text-[11px] font-medium uppercase">
        {provider.hasBalanceApi ? `Balance at ${provider.name}` : 'Balance'}
        {provider.hasBalanceApi && (
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            aria-label="Read the balance from the provider now"
            title="Read the balance from the provider now"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(provider.id)}
          >
            <RefreshCw
              className={`size-3.5 ${refresh.isPending ? 'animate-spin' : ''}`}
              aria-hidden
            />
          </Button>
        )}
      </dt>
      <dd className="mt-1">
        {neverRead ? (
          <p className="text-muted-foreground text-sm">Not read from {provider.name} yet.</p>
        ) : (
          <span className="flex flex-wrap items-center gap-2 text-lg font-semibold tabular-nums">
            {formatCurrency(provider.balance, currency)}
            {provider.isLowBalance && <StatusBadge variant="danger">Low balance</StatusBadge>}
            {provider.hasBalanceApi && !provider.isBalanceCurrent && (
              <StatusBadge variant="warning">Not current</StatusBadge>
            )}
          </span>
        )}
        {provider.hasBalanceApi && provider.balanceCheckedAtUtc && (
          <p className="text-muted-foreground text-xs">
            Read from {provider.name} {formatRelativeDate(provider.balanceCheckedAtUtc)} (
            {formatDateTime(provider.balanceCheckedAtUtc, DEFAULT_TIME_ZONE)})
          </p>
        )}
        {provider.hasBalanceApi && !provider.isBalanceCurrent && provider.balanceSyncError && (
          <p className="text-status-warning text-xs">
            Last read failed {formatRelativeDate(provider.balanceSyncFailedAtUtc)}:{' '}
            {provider.balanceSyncError}. The last balance read is kept and checkout still checks the
            provider before every payment.
          </p>
        )}
      </dd>
    </div>
  );
}
